import React, { forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { makeStyles } from "tss-react/mui";

import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";

import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";

import Switch from "../../../../../Common/Switch";
import { showToast } from "../../../../../../store/actions";
import { regexName } from "../../../../../../util/regularExpress";
import Footer from "./Footer";
import DSContext from "../DSContext";
import { DATA_SOURCE } from "../../../../../../util/Data/DataModeling";

const fieldRequiredArr = [
  { text: "Yes", value: true },
  { text: "No field required check", value: false },
];

const uniqueArr = [
  { text: "Yes", value: true },
  { text: "No unique check", value: false },
];

const useStyles = makeStyles()((theme) => ({
  heading: {
    color: "#DF672A",
    fontSize: "1rem",
  },
  FormControl: {
    display: "flex",
    flexDirection: "row",
  },
  menuItem: {
    "& > .MuiSvgIcon-root": {
      display: "inline-block",
      marginRight: "3px",
    },
  },
  select: {
    "& > .MuiSelect-select": {
      display: "flex",
      alignItems: "center",

      "& > .MuiSvgIcon-root": {
        display: "inline-block",
        marginRight: "3px",
      },
    },
  },
  Min: {
    "& p": {
      whiteSpace: "nowrap",
      marginLeft: 0,
    },
  },
}));

const SelectCategory = ({ alerts, value, setValue, disabled }) => {
  const { classes } = useStyles();

  return (
    <Select
      value={value}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      label="Category"
      disabled={disabled}
      className={classes.select}
    >
      {alerts.map((alert) => (
        <MenuItem
          key={alert.value}
          value={alert.value}
          disabled={alert.value === "NoOption"}
          className={classes.menuItem}
        >
          {alert.value === "NoOption" ? (
            <></>
          ) : (
            <>
              {alert.value === "Critical" ? (
                <InfoIcon color="error" fontSize="small" />
              ) : (
                <WarningIcon color="warning" fontSize="small" />
              )}{" "}
            </>
          )}
          {alert.text}
        </MenuItem>
      ))}
    </Select>
  );
};

const DQM = ({ onClose, addField, currentField }, ref) => {
  const { fields, dataSourceId, name: datasetName } = useContext(DSContext);
  const { dqmAlerts, dqmCharsets } = useSelector((state) => state.config);
  const dataSourceData = useSelector((state) => state.dataSourceData);

  const dispatch = useDispatch();
  const dataSources = dataSourceData.dataSources;

  // State Data for DQM Tab
  const [dqm, setDqm] = useState(false);
  const [required, setRequired] = useState(false);
  const [charset, setCharset] = useState("NoOption");
  const [regex, setRegex] = useState("");
  const [fieldRequiredAlert, setFieldRequiredAlert] = useState("NoOption");
  const [charsetAlert, setCharsetAlert] = useState("NoOption");
  const [unique, setUnique] = useState(false);
  const [uniqueAlert, setUniqueAlert] = useState("NoOption");
  const [refSource, setRefSource] = useState("NoOption");
  const [refField, setRefField] = useState("NoOption");
  const [refAlert, setRefAlert] = useState("NoOption");
  const [lengthCheck, setLengthCheck] = useState(false);
  const [lengthCheckAlert, setLengthCheckAlert] = useState("NoOption");
  const [lengthError, setLengthError] = useState(false);
  const [min, setMin] = useState(0);
  const [max, setMax] = useState(0);

  const enableDQMSave = () => {
    const fieldRequired = required && fieldRequiredAlert;
    const charsetRequired = charset && charset !== "NoOption";
    const uniqueRequired = unique && unique !== "NoOption";
    const refRequired = refSource && refSource !== "NoOption";

    return !(
      !dqm ||
      (dqm &&
        (fieldRequired || charsetRequired || uniqueRequired || refRequired || lengthCheck) &&
        (!fieldRequired || (fieldRequired && fieldRequiredAlert !== "NoOption")) &&
        (!charsetRequired ||
          (charsetRequired &&
            charsetAlert &&
            charsetAlert !== "NoOption" &&
            (charset !== "Custom" || (charset === "Custom" && regex.trim())))) &&
        (!uniqueRequired || (uniqueRequired && uniqueAlert && uniqueAlert !== "NoOption")) &&
        (!refRequired || (refRequired && refField && refField !== "NoOption" && refAlert && refAlert !== "NoOption")))
    );
  };

  useImperativeHandle(ref, () => ({ enableDQMSave, handleSaveDetails }));

  const charsets = [
    {
      value: "NoOption",
      text: "Select Charset",
    },
    {
      value: false,
      text: "No charset check",
    },
    ...dqmCharsets,
  ];

  const alerts = [
    {
      value: "NoOption",
      text: "Select Category",
    },
    ...dqmAlerts,
  ];

  const dataSourcesArr = useMemo(() => {
    //Removing current dataset
    const filtered = dataSources.filter((source) => source.id !== dataSourceId);
    const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));

    return [
      {
        value: "NoOption",
        text: "Select Source",
      },
      {
        value: false,
        text: "No source check",
      },
      {
        value: dataSourceId,
        text: datasetName + " (Current Dataset)",
      },
      ...sorted.map((source) => ({
        value: source.id,
        text: source.name,
      })),
    ];
  }, [dataSourceId, dataSources, datasetName]);

  const dsFields = useMemo(() => {
    const array = [
      {
        value: "NoOption",
        text: "Select Field",
      },
    ];

    if (refSource === dataSourceId) {
      const filtered = fields.filter(({ name }) => name !== currentField);
      const sorted = filtered.sort((a, b) => a.name.localeCompare(b.name));

      array.push(
        {
          value: false,
          text: "No field check",
        },
        ...sorted.map((field) => ({ value: field.id, text: field.name }))
      );
    } else {
      const source = dataSources.find((source) => source.id === refSource);
      if (source) {
        const sorted = [...source.fields].sort((a, b) => a.name.localeCompare(b.name));

        array.push(
          {
            value: false,
            text: "No field check",
          },
          ...sorted.map((field) => ({ value: field.id, text: field.name }))
        );
      }
    }

    return array;
  }, [refSource, dataSourceId, dataSources, fields, currentField]);

  const { classes } = useStyles();

  useEffect(() => {
    const tField = fields.find((field) => field.name === currentField);
    if (tField.dqm && Object.keys(tField.dqm).length) {
      const {
        required,
        fieldRequiredAlert,
        charsetAlert,
        charset,
        fieldRequired,
        regex,
        unique,
        lengthCheck,
        lengthCheckAlert,
        min,
        max,
        uniqueAlert,
        refSource,
        refField,
        refAlert,
      } = tField.dqm;

      setDqm(required);
      if (required) {
        setRequired(fieldRequired || false);
        setFieldRequiredAlert(fieldRequiredAlert || "NoOption");
        setCharset(charset || "NoOption");
        setCharsetAlert(charsetAlert || "NoOption");
        setUnique(unique || false);
        setUniqueAlert(uniqueAlert || "NoOption");
        setRegex(regex);
        setRefSource(refSource || "NoOption");
        setRefField(refField || "NoOption");
        setRefAlert(refAlert || "NoOption");
        setLengthCheck(lengthCheck || false);
        setLengthCheckAlert(lengthCheckAlert || "NoOption");
        setMin(min || 0);
        setMax(max || 0);
      }
    }
  }, [currentField, fields]);

  const resetFields = () => {
    setRequired(false);
    setCharset("NoOption");
    setRegex("");
    setFieldRequiredAlert("NoOption");
    setCharsetAlert("NoOption");
    setUnique(false);
    setUniqueAlert("NoOption");
    setRefSource("NoOption");
    setRefField("NoOption");
    setRefAlert("NoOption");
    setLengthCheck(false);
    setMax(0);
    setMin(0);
  };

  const handleSaveDetails = (saveBy) => {
    if (min > max) return setLengthError(true);

    setLengthError(false);

    const tField = fields.find((field) => field.name === currentField);
    addField({
      ...tField,
      dqm: {
        HealthPercentage: tField.dqm?.HealthPercentage,
        RecCount: tField.dqm?.RecCount,
        required: dqm,
        ...(dqm
          ? {
              fieldRequired: required,
              charset: charset === "NoOption" ? false : charset,
              unique: unique === "NoOption" ? false : unique,
              fieldRequiredAlert: fieldRequiredAlert === "NoOption" ? false : fieldRequiredAlert,
              charsetAlert: charsetAlert === "NoOption" ? false : charsetAlert,
              uniqueAlert: uniqueAlert === "NoOption" ? false : uniqueAlert,
              regex: charset === "Custom" ? regex : "",
              refSource: refSource === "NoOption" ? false : refSource,
              refField: refField === "NoOption" ? false : refField,
              refAlert: refAlert === "NoOption" ? false : refAlert,
              lengthCheckAlert: lengthCheckAlert === "NoOption" ? false : lengthCheckAlert,
              lengthCheck,
              min,
              max,
            }
          : {
              fieldRequired: false,
              charset: "",
              unique: "",
              fieldRequiredAlert: "",
              charsetAlert: "",
              uniqueAlert: "",
              regex: "",
              refSource: "",
              refField: "",
              refAlert: "",
              lengthCheckAlert: "",
              lengthCheck: false,
              min: 0,
              max: 0,
            }),
      },
    });

    if (saveBy !== "auto") {
      dispatch(
        showToast({
          message: `Attribute ${currentField ? "saved" : "added"} successfully.`,
          type: "success",
        })
      );
      onClose();
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", height: "auto", marginTop: "30px" }} role="presentation">
        <>
          <FormControl className={classes.FormControl} sx={{ marginTop: "10px" }} component="fieldset">
            <FormGroup aria-label="position" row>
              <FormControlLabel
                value={dqm}
                control={<Switch />}
                label="DQM"
                labelPlacement="start"
                sx={{ margin: "0" }}
                onChange={(e) => {
                  setDqm(!dqm);
                  resetFields();
                }}
                checked={dqm}
              />
            </FormGroup>
          </FormControl>

          <Typography className={classes.heading}>Completeness</Typography>
          <Grid container spacing={1}>
            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Field Required</InputLabel>
                <Select
                  value={required}
                  onChange={(e) => {
                    if (required) setFieldRequiredAlert("NoOption");
                    else setFieldRequiredAlert("Acceptable");

                    setRequired((prev) => !prev);
                  }}
                  label="Field Required"
                  disabled={!dqm}
                  width="medium"
                >
                  {fieldRequiredArr.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Category</InputLabel>
                <SelectCategory
                  alerts={alerts}
                  value={fieldRequiredAlert}
                  setValue={setFieldRequiredAlert}
                  disabled={!dqm || !required || required === "NoOption"}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Typography className={classes.heading}>Validity</Typography>
          <Grid container spacing={1}>
            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Charset</InputLabel>
                <Select
                  value={charset}
                  onChange={(e) => {
                    if (e.target.value === false) setCharsetAlert("NoOption");
                    else if (!charset || charset === "NoOption") setCharsetAlert("Acceptable");

                    setCharset(e.target.value);
                  }}
                  label="Charset"
                  disabled={!dqm}
                >
                  {charsets.map((charset) => (
                    <MenuItem key={charset.value} value={charset.value} disabled={charset.value === "NoOption"}>
                      {charset.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Category</InputLabel>
                <SelectCategory
                  alerts={alerts}
                  value={charsetAlert}
                  setValue={setCharsetAlert}
                  disabled={!dqm || !charset || charset === "NoOption"}
                />
              </FormControl>
            </Grid>
          </Grid>

          {charset === "Custom" && (
            <TextField
              required
              value={regex}
              inputProps={{ maxLength: 300 }}
              onChange={(e) => setRegex(e.target.value)}
              fullWidth
              multiline
              label="Custom RegEx"
              variant="outlined"
              sx={{ my: 2 }}
              disabled={!dqm}
            />
          )}

          <Grid container spacing={1}>
            <Grid size={12}>
              <Checkbox
                value={lengthCheck}
                size="medium"
                color="info"
                onChange={() => {
                  if (lengthCheck) {
                    setMin(0);
                    setMax(0);
                    setLengthCheckAlert("NoOption");
                  } else {
                    setLengthCheckAlert("Acceptable");
                  }
                  setLengthCheck((prev) => !prev);
                }}
                checked={lengthCheck}
                disabled={!dqm}
              />
              <FormLabel>Length Check</FormLabel>
            </Grid>
          </Grid>

          <Grid container spacing={1}>
            <Grid size={3}>
              <TextField
                type="number"
                value={min}
                inputProps={{ maxLength: 10, min: 0 }}
                error={lengthError}
                helperText={lengthError ? "Max length should be higher than Min length" : ""}
                onChange={(e) => {
                  if (regexName(e.target.value, "numeric")) setMin(parseInt(e.target.value) || 0);
                }}
                fullWidth
                multiline
                label="Min"
                variant="outlined"
                className={classes.Min}
                sx={{ my: 2 }}
                disabled={!dqm || !lengthCheck}
              />
            </Grid>

            <Grid size={3}>
              <TextField
                type="number"
                value={max}
                inputProps={{ maxLength: 10, min: 0 }}
                error={lengthError}
                onChange={(e) => {
                  if (regexName(e.target.value, "numeric")) setMax(parseInt(e.target.value) || 0);
                }}
                fullWidth
                multiline
                label="Max"
                variant="outlined"
                sx={{ my: 2 }}
                disabled={!dqm || !lengthCheck}
              />
            </Grid>

            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Category</InputLabel>
                <SelectCategory
                  alerts={alerts}
                  value={lengthCheckAlert}
                  setValue={setLengthCheckAlert}
                  disabled={!dqm}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Typography className={classes.heading}>Uniqueness</Typography>
          <Grid container spacing={1}>
            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Unique</InputLabel>
                <Select
                  value={unique}
                  onChange={(e) => {
                    if (unique) setUniqueAlert("NoOption");
                    else setUniqueAlert("Acceptable");

                    setUnique(e.target.value);
                  }}
                  label="Unique"
                  disabled={!dqm}
                >
                  {uniqueArr.map((item) => (
                    <MenuItem key={item.value} value={item.value} disabled={item.value === "NoOption"}>
                      {item.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Category</InputLabel>
                <SelectCategory
                  alerts={alerts}
                  value={uniqueAlert}
                  setValue={setUniqueAlert}
                  disabled={!dqm || !unique || unique === "NoOption"}
                />
              </FormControl>
            </Grid>
          </Grid>

          <Typography className={classes.heading}>Consistency</Typography>
          <Grid container spacing={1}>
            <Grid size={3}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>{DATA_SOURCE.singular}</InputLabel>
                <Select
                  value={refSource}
                  onChange={(e) => {
                    if (e.target.value === false) setRefAlert("NoOption");

                    setRefSource(e.target.value);
                    setRefField("NoOption");
                  }}
                  label={DATA_SOURCE.singular}
                  disabled={!dqm}
                >
                  {dataSourcesArr.map((ds) => (
                    <MenuItem key={ds.value} value={ds.value} disabled={ds.value === "NoOption"}>
                      {ds.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={3}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Field</InputLabel>
                <Select
                  value={refField}
                  onChange={(e) => {
                    if (e.target.value === false) setRefAlert("NoOption");
                    else setRefAlert("Acceptable");

                    setRefField(e.target.value);
                  }}
                  label="Field"
                  disabled={!dqm || !refSource || refSource === "NoOption"}
                >
                  {dsFields.map((field) => (
                    <MenuItem key={field.value} value={field.value} disabled={field.value === "NoOption"}>
                      {field.text}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={6}>
              <FormControl required fullWidth variant="outlined" sx={{ my: 2 }}>
                <InputLabel>Category</InputLabel>
                <SelectCategory
                  alerts={alerts}
                  value={refAlert}
                  setValue={setRefAlert}
                  disabled={!dqm || !refSource || refSource === "NoOption" || !refField || refField === "NoOption"}
                />
              </FormControl>
            </Grid>
          </Grid>
        </>
      </Box>
      <Footer disabled={enableDQMSave()} handleSaveDetails={handleSaveDetails} onClose={onClose} />
    </>
  );
};

export default forwardRef(DQM);
